
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { updateUserProfile, uploadAvatar, fetchReferralCount, getBadgeLevel } from '@/utils/referralUtils';
import { toast } from 'sonner';
import { Edit, Upload, User, BadgeCheck } from 'lucide-react';
import { useEffect } from 'react';

interface ProfileEditorProps {
  memberData: {
    id: string;
    full_name: string; 
    nickname: string | null;
    avatar_url: string | null;
    invite_count: number;
    referred_by: string | null;
  };
  onUpdate: () => void;
}

const ProfileEditor = ({ memberData, onUpdate }: ProfileEditorProps) => {
  const { user } = useAuth();
  const [nickname, setNickname] = useState(memberData.nickname || '');
  const [avatarUrl, setAvatarUrl] = useState(memberData.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inviteCount, setInviteCount] = useState(memberData.invite_count || 0);
  
  useEffect(() => {
    if (user) {
      fetchReferralCount(user.id).then(count => setInviteCount(count));
    }
  }, [user]);
  
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !user) {
      return;
    }
    
    try {
      const file = e.target.files[0];
      setUploading(true);
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image must be less than 2MB");
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error("Only image files are allowed");
        return;
      }
      
      const result = await uploadAvatar(user.id, file);
      
      if (result.success && result.url) {
        setAvatarUrl(result.url);
        toast.success("Avatar uploaded successfully!");
      } else {
        toast.error("Failed to upload avatar");
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error("An error occurred while uploading");
    } finally {
      setUploading(false);
    }
  };
  
  const handleSaveProfile = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      const result = await updateUserProfile(user.id, { nickname });
      
      if (result.success) {
        toast.success("Profile updated successfully!");
        onUpdate();
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("An error occurred while saving");
    } finally {
      setSaving(false);
    }
  };
  
  const badge = getBadgeLevel(inviteCount);
  
  const getBadgeColor = (level: string) => {
    switch (level) {
      case 'bronze': return 'bg-amber-600 text-white';
      case 'silver': return 'bg-gray-400 text-white';
      case 'gold': return 'bg-yellow-500 text-white';
      case 'platinum': return 'bg-cyan-500 text-white';
      case 'legend': return 'bg-purple-600 text-white';
      default: return 'bg-gray-300 text-gray-700';
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <User className="mr-2" /> Profile Settings
        </CardTitle>
        <CardDescription>Update your nickname and avatar</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Current Status</Label>
            <div className="flex items-center gap-2">
              <BadgeCheck className={`${badge.level !== 'none' ? 'text-omoduduwa-terracotta' : 'text-gray-400'}`} />
              <span className={`px-2 py-1 rounded-full text-sm ${getBadgeColor(badge.level)}`}>
                {badge.label} Inviter
              </span>
              <span className="text-sm text-gray-500">
                ({inviteCount} {inviteCount === 1 ? 'invite' : 'invites'})
              </span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="avatar">Profile Picture</Label>
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} alt={nickname || memberData.full_name} />
                ) : null}
                <AvatarFallback className="bg-omoduduwa-terracotta text-white text-xl">
                  {(nickname || memberData.full_name).substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <Label htmlFor="avatar-upload" className="cursor-pointer">
                  <div className="flex items-center gap-2 hover:text-omoduduwa-terracotta transition-colors">
                    <Upload size={16} />
                    <span>{avatarUrl ? 'Change avatar' : 'Upload avatar'}</span>
                  </div>
                </Label>
                <Input 
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                  disabled={uploading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  JPEG, PNG or GIF. Max 2MB.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="nickname">Nickname</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Edit className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  id="nickname"
                  placeholder="Enter a nickname"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Button 
                onClick={handleSaveProfile}
                disabled={saving || !nickname}
              >
                Save
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              This name will be displayed instead of your full name across the platform
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileEditor;
